import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { USE_CASE_MAP } from "@/data/useCases";
import SeverityPill from "@/components/SeverityPill";
import { ArrowUpRight, Trash2 } from "lucide-react";

export default function HistoryPage() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setItems(data.reviews || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const removeReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      setItems((it) => (it || []).filter((r) => r.id !== id));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <section
      data-testid="history-page"
      className="max-w-[1200px] mx-auto px-6 md:px-10 py-12"
    >
      <div className="eyebrow mb-4">History</div>
      <h1 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
        Your saved reviews.
      </h1>
      <p className="mt-3 text-ink-70">
        Every review runs stays here — reopen it, copy the memo, or delete it.
      </p>

      {err && (
        <div className="mt-8 text-sm text-[var(--severity-red-text)] bg-[var(--severity-red-bg)] border border-[var(--severity-red-border)] px-3 py-2">
          {err}
        </div>
      )}

      {items === null && (
        <div className="mt-12 text-ink-50 font-mono-plex text-sm uppercase tracking-widest">
          Loading…
        </div>
      )}

      {items && items.length === 0 && (
        <div
          data-testid="history-empty"
          className="mt-16 paper-card text-center py-16"
        >
          <div className="eyebrow mb-4">Empty for now</div>
          <div className="font-serif-display text-2xl text-ink">
            You haven't saved any reviews yet.
          </div>
          <p className="mt-3 text-ink-70 max-w-md mx-auto">
            Upload a PDF or DOCX and we'll run the matching playbook. Signed-in reviews
            appear here automatically.
          </p>
          <button
            onClick={() => navigate("/")}
            className="btn-primary focus-ring mt-6"
            data-testid="history-upload-btn"
          >
            Upload a document
          </button>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="mt-10 border-t border-b border-ink-10">
          <div className="hidden md:grid grid-cols-12 gap-4 py-3 text-xs font-mono-plex uppercase tracking-widest text-ink-50 border-b border-ink-10">
            <div className="col-span-5">Document</div>
            <div className="col-span-3">Playbook</div>
            <div className="col-span-2">Risk</div>
            <div className="col-span-2 text-right">Date</div>
          </div>
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`history-row-${r.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 py-4 border-b border-ink-10 hover:bg-[rgba(10,10,10,0.02)] transition-colors items-center"
            >
              <div className="col-span-5">
                <button
                  onClick={() => navigate(`/history/${r.id}`)}
                  className="text-left group"
                  data-testid={`history-open-${r.id}`}
                >
                  <div className="font-medium text-ink group-hover:text-forest transition-colors truncate">
                    {r.filename}
                  </div>
                </button>
              </div>
              <div className="col-span-3 text-sm text-ink-70">
                {USE_CASE_MAP[r.document_type]?.label || r.document_type}
              </div>
              <div className="col-span-2 flex gap-2 flex-wrap">
                {["red", "amber", "green"].map((s) =>
                  (r.risk_counts?.[s] || 0) > 0 ? (
                    <span key={s} className={`pill pill-${s}`}>
                      <span className="dot" /> {r.risk_counts[s]}
                    </span>
                  ) : null
                )}
              </div>
              <div className="col-span-2 flex items-center justify-between md:justify-end gap-3">
                <span className="text-xs text-ink-50 font-mono-plex">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
                <button
                  onClick={() => navigate(`/history/${r.id}`)}
                  className="btn-ghost focus-ring"
                  data-testid={`history-open-btn-${r.id}`}
                >
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeReview(r.id)}
                  className="btn-ghost focus-ring text-ink-50 hover:text-[var(--severity-red-text)]"
                  data-testid={`history-delete-${r.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
